import React, { useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Dialog, NavBar, Toast } from 'antd-mobile';
import { useApolloClient } from '@apollo/client';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import { t } from '../../../i18n';
import { useTranslation } from 'react-i18next';
import { useTmsTaskWaypoint, useTmsTaskOperations, useTransportStore } from '../store/transportStore';
import { buildAddressFromAppointment } from '../../../utils/addressUtils';
import { formatTimestampByLanguage } from '../../../utils/dateTimeUtils';
import TransportInfoItem from '../components/TransportInfoItem';
import TransportPhotoUploadCard from '../components/TransportPhotoUploadCard';
import TransportPhotoViewer from '../components/TransportPhotoViewer';
import transportPhotoService, {
    TransportPhotoType,
    TransportPositionType,
    uploadTransportPhotosWithTimeout,
} from '../services/TransportPhotoService';
import { TmsPath, TmsPathPre } from '../../../enums/TmsNavigatePathEnum';
import { TmsOperationSourceEnum } from '../../../enums/TmsEnum';
import { translateResultCode } from '../../../utils/resultCode';
import { addWatermarkToPhoto, type WatermarkGpsCache } from '../services/TransportWatermarkService';
import { useConfigEnable } from '../../../graphql/queries/commonConfigQueries';
import {
    GRAY_CONFIG_TMS_DRIVER_PHOTO_WATERMARK_DISABLED,
    GRAY_CONFIG_TMS_DRIVER_WATERMARK_ANDROID_NATIVE_LOCATION_DISABLED,
    GRAY_CONFIG_TMS_DRIVER_WATERMARK_IOS_NATIVE_LOCATION_DISABLED,
} from '../../../constants/grayConfigKeys';

interface DepartingPageState {
    taskId: number;
    sealNumber?: string | null;
    parcelCount?: number;
    nextWaypointId?: string | null;
    nextWaypointType?: number | null;
}

const TransportDepartingPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { taskWayPointId } = useParams<{ taskWayPointId: string }>();
    const { i18n } = useTranslation();
    const state = (location.state as DepartingPageState) || {};
    const { taskId, sealNumber, parcelCount, nextWaypointId, nextWaypointType } = state;
    const apolloClient = useApolloClient();

    const { tmsTaskWaypoint } = useTmsTaskWaypoint(taskWayPointId || '');
    const { updateTmsTaskWaypointToDeparture } = useTmsTaskOperations();
    const refreshTmsTaskWaypointList = useTransportStore(s => s.refreshTmsTaskWaypointList);
    const { isEnabled: isWatermarkDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_PHOTO_WATERMARK_DISABLED);
    const { isEnabled: isAndroidNativeLocationDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_WATERMARK_ANDROID_NATIVE_LOCATION_DISABLED);
    const { isEnabled: isIosNativeLocationDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_WATERMARK_IOS_NATIVE_LOCATION_DISABLED);
    const watermarkGpsRef = useRef<WatermarkGpsCache | null>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [photos, setPhotos] = useState<Record<TransportPositionType, TransportPhotoType | undefined>>(
        {} as Record<TransportPositionType, TransportPhotoType | undefined>
    );

    const takePhoto = async (type: TransportPositionType, file: File) => {
        try {
            setIsLoading(true);
            let watermarkedFile = file;
            if (!isWatermarkDisabled) {
                const result = await addWatermarkToPhoto(file, {
                    ianaTimeZone: tmsTaskWaypoint?.waypointTimezoneLocation,
                    apolloClient,
                    cachedGps: watermarkGpsRef.current,
                    language: i18n.language,
                    androidNativeLocationDisabled: isAndroidNativeLocationDisabled,
                    iosNativeLocationDisabled: isIosNativeLocationDisabled,
                });
                watermarkedFile = result.file;
                if (result.gpsCache) {
                    watermarkGpsRef.current = result.gpsCache;
                }
            }
            await transportPhotoService.handlePhotoNotUpload(type, watermarkedFile, updateFn => {
                setPhotos(prev => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const deletePhoto = (type: TransportPositionType) => {
        Dialog.confirm({
            content: t('pages.tms.transporting.arriving.deleteContent'),
            confirmText: t('pages.tms.transporting.arriving.deleteConfirm'),
            cancelText: t('pages.tms.transporting.arriving.deleteCancel'),
            onConfirm: () => {
                transportPhotoService.handleDeletePhoto(type, updateFn => {
                    setPhotos(prev => updateFn(prev));
                });
            },
        });
    };

    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    const isValidPhoto = (item: TransportPhotoType | undefined): item is TransportPhotoType =>
        item?.objectKey !== undefined && item?.url !== undefined;

    const sealPhotoItems = [
        photos.departureSealPhoto1,
        photos.departureSealPhoto2,
        photos.departureSealPhoto3,
    ].filter(isValidPhoto);

    const bolPhotoItems = [
        photos.departureBolPhoto1,
        photos.departureBolPhoto2,
        photos.departureBolPhoto3,
    ].filter(isValidPhoto);

    const hasEnoughPhotos = () => sealPhotoItems.length >= 1 && bolPhotoItems.length >= 1;

    const handleConfirmDepart = async () => {
        if (!taskWayPointId || !taskId) return;

        if (!hasEnoughPhotos()) {
            Toast.show({ content: t('pages.tms.transporting.departing.photoRequired'), position: 'bottom' });
            return;
        }

        setIsLoading(true);
        try {
            // 上传封签照片
            const sealPhotoKeys = await uploadTransportPhotosWithTimeout(sealPhotoItems);
            // 上传 BOL 照片（复用上传工具，不重复显示 Toast）
            const bolPhotoKeys = await uploadTransportPhotosWithTimeout(bolPhotoItems, 5000, false);

            const result = await updateTmsTaskWaypointToDeparture({
                tmsTaskWayPointId: parseInt(taskWayPointId),
                taskId,
                departureSealPhotosJson: sealPhotoKeys,
                departurePhotosJson: bolPhotoKeys,
                operationSource: TmsOperationSourceEnum.APP,
            });

            if (result.success) {
                Toast.show({ content: t('pages.tms.transporting.departing.submitSuccess'), position: 'bottom' });
                await refreshTmsTaskWaypointList();
                if (nextWaypointId) {
                    navigate(`${TmsPathPre.TMS_TRANSPORTING_DETAIL_PRE}${nextWaypointId}/${nextWaypointType || '2'}`);
                } else {
                    navigate(TmsPath.TMS_TRANSPORTING);
                }
            } else {
                const errorMsg = translateResultCode(result.code, result.message);
                Toast.show({ content: errorMsg || t('pages.tms.transporting.departing.submitFailed'), position: 'center' });
            }
        } catch (error: any) {
            console.error('TransportDepartingPage submit error:', error);
            Toast.show({ content: t('pages.tms.transporting.departing.submitFailed'), position: 'bottom' });
        } finally {
            setIsLoading(false);
        }
    };

    const address = tmsTaskWaypoint
        ? buildAddressFromAppointment({
              addressLine1: tmsTaskWaypoint.addressLine1 ?? undefined,
              addressLine2: tmsTaskWaypoint.addressLine2 ?? undefined,
              city: tmsTaskWaypoint.city ?? undefined,
              state: tmsTaskWaypoint.state ?? undefined,
              zipcode: tmsTaskWaypoint.zipcode ?? undefined,
              country: tmsTaskWaypoint.country ?? undefined,
          })
        : '';

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>
                    {t('pages.tms.transporting.departing.title')}
                </NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 站点信息 */}
                {tmsTaskWaypoint && (
                    <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                        <h2 className="text-base font-semibold mb-1">
                            {t('pages.tms.transporting.departing.siteInfo')}
                        </h2>
                        <TransportInfoItem
                            label={t('pages.tms.transporting.arriving.name')}
                            value={tmsTaskWaypoint.wayPointName || '-'}
                        />
                        <TransportInfoItem
                            label={t('pages.tms.transporting.arriving.contact')}
                            value={tmsTaskWaypoint.contactName || '-'}
                        />
                        <TransportInfoItem
                            label={t('pages.tms.transporting.arriving.phone')}
                            value={`${tmsTaskWaypoint.contactPhone || ''}${tmsTaskWaypoint.ext ? `-${tmsTaskWaypoint.ext}` : ''}`}
                        />
                        <TransportInfoItem
                            label={t('pages.tms.transporting.arriving.address')}
                            value={address}
                        />
                        <TransportInfoItem
                            label={t('pages.tms.transporting.quantity')}
                            value={String(parcelCount ?? '-')}
                        />
                        <TransportInfoItem
                            label={t('pages.tms.transporting.sealNumber')}
                            value={sealNumber || '-'}
                        />
                        {tmsTaskWaypoint.planDepartureTime && (
                            <TransportInfoItem
                                label={t('pages.tms.transporting.planDepartureTime')}
                                value={
                                    formatTimestampByLanguage(tmsTaskWaypoint.planDepartureTime, i18n.language) +
                                    (tmsTaskWaypoint.planDepartureTimezone
                                        ? ` (${tmsTaskWaypoint.planDepartureTimezone})`
                                        : '')
                                }
                            />
                        )}
                    </div>
                )}

                {/* 封签照片 */}
                <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">
                        {t('pages.tms.transporting.departing.sealPhotos')}
                    </h2>
                    <p className="text-sm text-gray-600 mb-1">
                        <span>{t('pages.tms.transporting.departing.sealPhotoDesc')}</span>
                        <span className="text-green-600 text-base">{t('pages.tms.transporting.departing.sealPhotoCount')}</span>
                        <span>{t('pages.tms.transporting.departing.sealPhotoDesc2')}</span>
                        <span className="text-green-600 text-base">{t('pages.tms.transporting.departing.sealPhotoRequirement')}</span>
                        <span>{t('pages.tms.transporting.departing.sealPhotoDesc3')}</span>
                    </p>
                    <div className="grid grid-cols-3 gap-2 my-4">
                        {(['departureSealPhoto1', 'departureSealPhoto2', 'departureSealPhoto3'] as TransportPositionType[]).map(type => (
                            <TransportPhotoUploadCard
                                key={type}
                                photoUrl={photos[type]?.url}
                                type={type}
                                description=""
                                onTakePhoto={takePhoto}
                                onDelete={deletePhoto}
                                onView={handleViewPhoto}
                                cameraOnly
                            />
                        ))}
                    </div>
                </div>

                {/* BOL 照片 */}
                <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">
                        {t('pages.tms.transporting.departing.bolPhotos')}
                    </h2>
                    <p className="text-sm text-gray-600 mb-1">
                        <span>{t('pages.tms.transporting.departing.bolPhotoDesc')}</span>
                        <span className="text-green-600 text-base">{t('pages.tms.transporting.departing.bolPhotoCount')}</span>
                        <span>{t('pages.tms.transporting.departing.bolPhotoDesc2')}</span>
                        <span className="text-green-600 text-base">{t('pages.tms.transporting.departing.bolPhotoRequirement')}</span>
                        <span>{t('pages.tms.transporting.departing.bolPhotoDesc3')}</span>
                    </p>
                    <div className="grid grid-cols-3 gap-2 my-4">
                        {(['departureBolPhoto1', 'departureBolPhoto2', 'departureBolPhoto3'] as TransportPositionType[]).map(type => (
                            <TransportPhotoUploadCard
                                key={type}
                                photoUrl={photos[type]?.url}
                                type={type}
                                description=""
                                onTakePhoto={takePhoto}
                                onDelete={deletePhoto}
                                onView={handleViewPhoto}
                                cameraOnly
                            />
                        ))}
                    </div>
                </div>

                <TransportPhotoViewer photoUrl={viewingPhoto} onClose={() => setViewingPhoto(null)} />
            </div>

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={!hasEnoughPhotos() || isLoading}
                    onClick={handleConfirmDepart}
                >
                    {t('pages.tms.transporting.departing.confirm')}
                </Button>
            </div>
        </div>
    );
};

export default TransportDepartingPage;
